import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { CravyChatbot } from './components/ui/cravy-chatbot';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';

// Public Pages (RuchiBox Features)
import RuchiBoxPromise from './pages/RuchiBoxPromise';
import HomeCookCode from './pages/HomeCookCode';
import BuyerCommitment from './pages/BuyerCommitment';
import VendorCommitment from './pages/VendorCommitment';
import RuchiBoxPoll from './pages/RuchiBoxPoll';
import RuchiBoxBazaar from './pages/RuchiBoxBazaar';
import RuchiBoxDigest from './pages/RuchiBoxDigest';

// Buyer Pages
import BuyerHome from './pages/buyer/BuyerHome';
import CartPage from './pages/buyer/CartPage';
import OrdersPage from './pages/buyer/OrdersPage';
import BuyerProfile from './pages/buyer/BuyerProfile';
import BuyerInsights from './pages/buyer/BuyerInsights';
import SellerDetail from './pages/buyer/SellerDetail';

// Seller Pages
import SellerDashboard from './pages/seller/SellerDashboard';
import SellerItems from './pages/seller/SellerItems';
import ItemForm from './pages/seller/ItemForm';
import SellerOrders from './pages/seller/SellerOrders';
import SellerProfile from './pages/seller/SellerProfile';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import EntitiesList from './pages/admin/EntitiesList';
import EntityForm from './pages/admin/EntityForm';
import UsersList from './pages/admin/UsersList';
import AdminOrders from './pages/admin/AdminOrders';
import AdminSettings from './pages/admin/AdminSettings';
import AdminBazaar from './pages/admin/AdminBazaar';
import AdminFAQs from './pages/admin/AdminFAQs';

// Shared Pages
import NotificationsPage from './pages/shared/NotificationsPage';

// Layouts
import BuyerLayout from './components/layouts/BuyerLayout';
import SellerLayout from './components/layouts/SellerLayout';
import AdminLayout from './components/layouts/AdminLayout';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const dashboardRoutes = {
      buyer: '/buyer',
      seller: '/seller',
      dealer: '/dealer',
      admin: '/admin'
    };
    return <Navigate to={dashboardRoutes[user.role] || '/'} replace />;
  }
  
  return children;
};

// Redirect authenticated users
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    const dashboardRoutes = {
      buyer: '/buyer',
      seller: '/seller',
      dealer: '/dealer',
      admin: '/admin'
    };
    return <Navigate to={dashboardRoutes[user.role] || '/'} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      
      {/* RuchiBox Feature Pages (Public) */}
      <Route path="/promise" element={<RuchiBoxPromise />} />
      <Route path="/promise/home-cook" element={<HomeCookCode />} />
      <Route path="/promise/buyer" element={<BuyerCommitment />} />
      <Route path="/promise/vendor" element={<VendorCommitment />} />
      <Route path="/poll" element={<RuchiBoxPoll />} />
      <Route path="/bazaar" element={<RuchiBoxBazaar />} />
      <Route path="/digest" element={<RuchiBoxDigest />} />
      
      {/* Buyer Routes */}
      <Route path="/buyer" element={
        <ProtectedRoute allowedRoles={['buyer']}>
          <BuyerHome />
        </ProtectedRoute>
      } />
      <Route path="/buyer/cart" element={
        <ProtectedRoute allowedRoles={['buyer']}>
          <CartPage />
        </ProtectedRoute>
      } />
      <Route path="/buyer/orders" element={
        <ProtectedRoute allowedRoles={['buyer']}>
          <OrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/buyer/profile" element={
        <ProtectedRoute allowedRoles={['buyer']}>
          <BuyerProfile />
        </ProtectedRoute>
      } />
      <Route path="/buyer/insights" element={
        <ProtectedRoute allowedRoles={['buyer']}>
          <BuyerInsights />
        </ProtectedRoute>
      } />
      <Route path="/buyer/notifications" element={
        <ProtectedRoute allowedRoles={['buyer']}>
          <BuyerLayout>
            <NotificationsPage />
          </BuyerLayout>
        </ProtectedRoute>
      } />
      <Route path="/buyer/seller/:sellerId" element={
        <ProtectedRoute allowedRoles={['buyer']}>
          <SellerDetail />
        </ProtectedRoute>
      } />
      
      {/* Seller Routes */}
      <Route path="/seller" element={
        <ProtectedRoute allowedRoles={['seller']}>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/seller/items" element={
        <ProtectedRoute allowedRoles={['seller']}>
          <SellerItems />
        </ProtectedRoute>
      } />
      <Route path="/seller/items/new" element={
        <ProtectedRoute allowedRoles={['seller']}>
          <ItemForm />
        </ProtectedRoute>
      } />
      <Route path="/seller/items/:id/edit" element={
        <ProtectedRoute allowedRoles={['seller']}>
          <ItemForm />
        </ProtectedRoute>
      } />
      <Route path="/seller/orders" element={
        <ProtectedRoute allowedRoles={['seller']}>
          <SellerOrders />
        </ProtectedRoute>
      } />
      <Route path="/seller/profile" element={
        <ProtectedRoute allowedRoles={['seller']}>
          <SellerProfile />
        </ProtectedRoute>
      } />
      <Route path="/seller/notifications" element={
        <ProtectedRoute allowedRoles={['seller']}>
          <SellerLayout>
            <NotificationsPage />
          </SellerLayout>
        </ProtectedRoute>
      } />
      
      {/* Dealer Routes */}
      <Route path="/dealer" element={
        <ProtectedRoute allowedRoles={['dealer']}>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dealer/items" element={
        <ProtectedRoute allowedRoles={['dealer']}>
          <SellerItems />
        </ProtectedRoute>
      } />
      <Route path="/dealer/items/new" element={
        <ProtectedRoute allowedRoles={['dealer']}>
          <ItemForm />
        </ProtectedRoute>
      } />
      <Route path="/dealer/items/:id/edit" element={
        <ProtectedRoute allowedRoles={['dealer']}>
          <ItemForm />
        </ProtectedRoute>
      } />
      <Route path="/dealer/orders" element={
        <ProtectedRoute allowedRoles={['dealer']}>
          <SellerOrders />
        </ProtectedRoute>
      } />
      <Route path="/dealer/profile" element={
        <ProtectedRoute allowedRoles={['dealer']}>
          <SellerProfile />
        </ProtectedRoute>
      } />
      <Route path="/dealer/notifications" element={
        <ProtectedRoute allowedRoles={['dealer']}>
          <SellerLayout>
            <NotificationsPage />
          </SellerLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/entities" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <EntitiesList />
        </ProtectedRoute>
      } />
      <Route path="/admin/entities/new" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <EntityForm />
        </ProtectedRoute>
      } />
      <Route path="/admin/entities/:id/edit" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <EntityForm />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <UsersList />
        </ProtectedRoute>
      } />
      <Route path="/admin/orders" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminOrders />
        </ProtectedRoute>
      } />
      <Route path="/admin/notifications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLayout>
            <NotificationsPage />
          </AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/bazaar" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminBazaar />
        </ProtectedRoute>
      } />
      <Route path="/admin/faqs" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminFAQs />
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
          <CravyChatbot />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
